import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	Card,
	Form,
	Heading,
	Modal,
	Paragraph,
	ScreenReaderText,
} from '@mlc/web-ui-toolkit';
import React, { Fragment, useRef, useState } from 'react';
import { showError } from '../../../../events/NotificationEmitter';
import {
	UpdateAccountRes,
	updatePassword,
} from '../../../../logic/functions/epiAccounts.function';
import {
	addFormEvent,
	EVENT_FORM_ABANDONED,
	EVENT_FORM_COMPLETED,
} from '../../../../logic/services/digital-data.service';
import ToastContainer from '../../../containers/Notifications/ToastContainer';
import { SomethingWentWrong } from '../../Common/Text/SomethingWentWrong';
import EpiTestIds from '../Account/Account.elements';
import ActionButtons from '../Common/ActionButtons';
import HiddenUsername from '../Common/HiddenUsername';
import PasswordForm from './PasswordForm';

type Props = {
	token: string;
	onModeChange?: () => void;
};

const UpdatePassword: React.FunctionComponent<Props> = ({
	token,
	onModeChange,
}) => {
	const FORM_ID = 'epiPasswordForm';
	const [updating, setUpdating] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [ariaMessage, setAriaMessage] = useState('');
	const [password, setPassword] = useState('');
	const [isCriteriaMet, setIsCriteriaMet] = useState(true);
	const [passwordValid, setPasswordValid] = useState(true);

	const actionButtonRef = useRef<any>();

	const complete = () => {
		addFormEvent(EVENT_FORM_COMPLETED, FORM_ID);
		switchMode();
	};

	const onCancelled = () => {
		addFormEvent(EVENT_FORM_ABANDONED, FORM_ID);
		switchMode();
	};

	const switchMode = () => {
		onModeChange && onModeChange();
	};

	const onUpdateHandler = async () => {
		if (validate()) {
			setAriaMessage('Processing please wait');
			setUpdating(true);
			const res = await updatePassword(token, password);
			setUpdating(false);
			if (UpdateAccountRes.SUCCESS === res) {
				setIsOpen(true);
			} else {
				showError(<SomethingWentWrong />, focus);
				focus();
			}
		}
	};

	const validate = (): boolean => {
		if (password.length === 0) {
			setIsCriteriaMet(true);
		}
		const passwordValid = password.length > 0 && isCriteriaMet;
		setPasswordValid(passwordValid);
		return passwordValid;
	};

	const passwordHandler = (password: string, isCriteriaMet: boolean) => {
		setPassword(password);
		setIsCriteriaMet(isCriteriaMet);
	};

	const onModalClose = () => {
		setIsOpen(false);
		complete();
		setAriaMessage('.Your password has been accepted');
	};

	const focus = () => {
		if (actionButtonRef.current) {
			actionButtonRef.current.focus();
		}
	};

	return (
		<Fragment>
			<Form id={FORM_ID} onSubmit={(e: any) => e.preventDefault()}>
				<ToastContainer />
				<ScreenReaderText
					id="passwordUpdateForm"
					role="region"
					aria-live="assertive"
				>
					{ariaMessage}
				</ScreenReaderText>
				<HiddenUsername />
				<PasswordForm
					mode={'update'}
					inputHandler={passwordHandler}
					isCriteriaMet={isCriteriaMet}
					isInvalid={!passwordValid}
				/>
				<ActionButtons
					onCancelClick={onCancelled}
					onUpdateClick={onUpdateHandler}
					isUpdating={updating}
					ariaLiveId="passwordUpdateForm"
					forwardedRef={actionButtonRef}
				/>
			</Form>
			<Modal isOpen={isOpen} onRequestClose={onModalClose} title={''}>
				<Card display="flex" flexDirection="column" alignItems="center">
					<FontAwesomeIcon icon={faCheck} size="3x" color="green" />
					<Heading
						el="h2"
						fontSize="little"
						fontWeight="medium"
						marginTop={2}
						marginBottom={6}
						data-id={EpiTestIds.confirmationHeading}
					>
						Your password has been updated
					</Heading>
					<Paragraph>
						Please update your financial planning software with your new
						password.
					</Paragraph>
				</Card>
			</Modal>
		</Fragment>
	);
};

export default UpdatePassword;
